import { useEffect, useState, type ComponentType } from "react";
import { LoginScreen } from "./screen/login";
import { InfoTransfer } from "./screen/info-transfer";
import { graphClient } from "./services/graph-client";
import { profileService } from "./services/profile";

type AuthStatus = "checking" | "authorized" | "unauthorized";
type UnauthorizedReason = "missing-token" | "invalid-token";

export const App = () => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>("checking");
  const [unauthorizedReason, setUnauthorizedReason] =
    useState<UnauthorizedReason>("missing-token");

  const LoginScreenWithReason = LoginScreen as ComponentType<{
    reason: UnauthorizedReason;
  }>;

  useEffect(() => {
    const validateSession = async () => {
      const searchParams = new URLSearchParams(window.location.search);

      const tokenFromParams = searchParams.get("token");
      const openFromApp = searchParams.get("openFromApp");
      const storedToken = localStorage.getItem("@token");
      const token = tokenFromParams ?? storedToken;

      if (openFromApp) {
        localStorage.setItem("@openFromApp", openFromApp);
      }

      if (!token) {
        localStorage.removeItem("@token");
        graphClient.setHeader("Authorization", "");
        setUnauthorizedReason("missing-token");
        setAuthStatus("unauthorized");
        return;
      }

      localStorage.setItem("@token", token);
      graphClient.setHeader("Authorization", `Bearer ${token}`);

      try {
        const response = await profileService.get();
        const user = response?.data;

        if (!user?.id) {
          throw new Error("Invalid token");
        }

        setAuthStatus("authorized");
      } catch {
        localStorage.removeItem("@token");
        graphClient.setHeader("Authorization", "");
        setUnauthorizedReason("invalid-token");
        setAuthStatus("unauthorized");
      }
    };

    validateSession();
  }, []);

  if (authStatus === "checking") {
    return null;
  }

  if (authStatus === "unauthorized") {
    return <LoginScreenWithReason reason={unauthorizedReason} />;
  }

  if (authStatus !== "authorized") {
    return null;
  }

  return <InfoTransfer />;
};
